// /steuern — Tabelle der Steuer-Termine + manueller Eintrag.
//
// Sprint 2 Tag 15:
// - Tabelle aller steuer_termin-Eintraege, sortiert nach datum.
// - Filter: nur kommende Termine (heute + 90T).
// - Severity-Badges: overdue/urgent/soon/scheduled.
// - Manueller Eintrag-Button → Form-Sektion (z.B. SOKA-Bau, Korrektur).
// - DELETE nur fuer manuelle (quelle='manual_*'); Pahl-Bescheide unloeschbar.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import {
  HoldingFirma,
  ManualSteuerTerminRequest,
  SteuerSeverity,
  SteuerTerminEntry,
  SteuerUpcomingResponse,
  UnauthorizedError,
  deleteSteuerTermin,
  fetchSteuerUpcoming,
  formatEUR,
  formatDate,
  postManualSteuer,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { FIRMA_DISPLAY } from "../services/firma_filter";

const FIRMA_OPTIONS: { value: HoldingFirma; label: string }[] = [
  { value: "drainx", label: "DrainX" },
  { value: "gundk", label: "G+K Sanitärtechnik" },
  { value: "xholding", label: "X-Holding UG" },
];

const ART_OPTIONS = [
  { value: "ust_va_2026_04", label: "USt-VA 04/2026 (manuell)" },
  { value: "ust_va_2026_05", label: "USt-VA 05/2026 (manuell)" },
  { value: "ust_va_2026_06", label: "USt-VA 06/2026 (manuell)" },
  { value: "kst_soli_q1", label: "KSt+Soli Q1" },
  { value: "kst_soli_q2", label: "KSt+Soli Q2" },
  { value: "kst_soli_q3", label: "KSt+Soli Q3" },
  { value: "kst_soli_q4", label: "KSt+Soli Q4" },
  { value: "gewst_q1", label: "GewSt Q1" },
  { value: "gewst_q2", label: "GewSt Q2" },
  { value: "gewst_q3", label: "GewSt Q3" },
  { value: "gewst_q4", label: "GewSt Q4" },
  { value: "lst_2026_04", label: "LSt 04/2026" },
  { value: "lst_2026_05", label: "LSt 05/2026" },
  { value: "lst_2026_06", label: "LSt 06/2026" },
  { value: "soka_bau", label: "SOKA-Bau" },
  { value: "tax_other", label: "Steuern (sonst.)" },
];

const SEVERITY_BADGE: Record<
  SteuerSeverity,
  { label: string; cls: string; rowCls: string }
> = {
  overdue: {
    label: "Überfällig",
    cls: "bg-rose-500/20 text-rose-200 border-rose-500/40",
    rowCls: "bg-rose-500/[0.08]",
  },
  urgent: {
    label: "<14T",
    cls: "bg-amber-500/20 text-amber-200 border-amber-500/40",
    rowCls: "bg-amber-500/[0.06]",
  },
  soon: {
    label: "30T",
    cls: "bg-sky-500/20 text-sky-200 border-sky-500/40",
    rowCls: "",
  },
  scheduled: {
    label: ">30T",
    cls: "bg-slate-700/40 text-slate-300 border-slate-600",
    rowCls: "",
  },
};

export default function SteuernPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [firmaFilter, setFirmaFilter] = useState<HoldingFirma | "">("");
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const upcomingQuery = useQuery<SteuerUpcomingResponse, Error>({
    queryKey: ["steuern", "upcoming", firmaFilter, includeOverdue],
    queryFn: () =>
      fetchSteuerUpcoming({
        horizon_days: 90,
        firma: firmaFilter || undefined,
        include_overdue: includeOverdue,
      }),
    enabled: auth.isAuthenticated,
    refetchInterval: 5 * 60_000,
    retry: (n, err) => !(err instanceof UnauthorizedError) && n < 1,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ firma, id }: { firma: HoldingFirma; id: string }) =>
      deleteSteuerTermin(firma, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["steuern"] }),
  });

  if (auth.isLoading) {
    return <FullPage>Session wird geprüft …</FullPage>;
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (upcomingQuery.error instanceof UnauthorizedError) {
    return <Navigate to="/login" replace />;
  }

  const data = upcomingQuery.data;
  const isLoading = upcomingQuery.isLoading;

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-md sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <h1 className="text-sm sm:text-base font-semibold text-slate-100 flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center min-h-11 px-2 -ml-2 text-sky-300 hover:text-sky-200"
            >
              ← Dashboard
            </Link>
            <span className="text-slate-500">·</span>
            <span>Steuer-Termine</span>
          </h1>
          <span className="text-xs text-slate-300">{auth.me?.email}</span>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-6 max-w-5xl mx-auto space-y-5">
        {/* Top-KPIs */}
        <section className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Termine 90T"
            value={data ? String(data.n_total) : isLoading ? "…" : "—"}
          />
          <KpiCard
            label="Summe 90T"
            value={data ? formatEUR(data.sum_eur) : isLoading ? "…" : "—"}
          />
          <KpiCard
            label="DrainX 90T"
            value={data ? formatEUR(data.by_firma["drainx"] ?? "0") : "—"}
            accent="sky"
          />
          <KpiCard
            label="G+K 90T"
            value={data ? formatEUR(data.by_firma["gundk"] ?? "0") : "—"}
            accent="violet"
          />
        </section>

        {/* Filter + Action-Bar */}
        <section className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-2 items-center text-sm">
            <label className="text-slate-300">Firma:</label>
            <select
              value={firmaFilter}
              onChange={(e) => setFirmaFilter(e.target.value as HoldingFirma | "")}
              className="rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-2 py-1 text-sm"
            >
              <option value="">alle</option>
              {FIRMA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="ml-3 inline-flex items-center gap-2 text-slate-300">
              <input
                type="checkbox"
                checked={includeOverdue}
                onChange={(e) => setIncludeOverdue(e.target.checked)}
                className="accent-sky-500"
              />
              Überfällige (30T) anzeigen
            </label>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-semibold px-4 min-h-11 text-sm"
          >
            {showForm ? "Form schließen" : "+ Manueller Eintrag"}
          </button>
        </section>

        {/* Manual-Form (collapsible) */}
        {showForm && (
          <ManualForm
            onCreated={() => {
              qc.invalidateQueries({ queryKey: ["steuern"] });
              setShowForm(false);
            }}
            onUnauth={() => navigate("/login", { replace: true })}
          />
        )}

        {/* Tabelle */}
        <section className="rounded-2xl border border-slate-800 bg-slate-950 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-300 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5">Datum</th>
                  <th className="text-left px-4 py-2.5">Firma</th>
                  <th className="text-left px-4 py-2.5">Art</th>
                  <th className="text-right px-4 py-2.5">Betrag</th>
                  <th className="text-left px-4 py-2.5">Quelle</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {isLoading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-slate-400 text-center">
                      Lade …
                    </td>
                  </tr>
                )}
                {!isLoading && data && data.entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-slate-400 text-center">
                      Keine Termine im Horizont. Manueller Eintrag oben?
                    </td>
                  </tr>
                )}
                {data?.entries.map((e) => (
                  <Row
                    key={e.id}
                    entry={e}
                    onDelete={
                      e.quelle.startsWith("manual_")
                        ? () =>
                            deleteMutation.mutate({
                              firma: e.firma_id,
                              id: e.id,
                            })
                        : null
                    }
                    deleting={deleteMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {upcomingQuery.error && !(upcomingQuery.error instanceof UnauthorizedError) && (
          <p className="text-sm text-rose-300">
            Fehler beim Laden: {upcomingQuery.error.message}
          </p>
        )}
      </main>
    </div>
  );
}

function Row({
  entry,
  onDelete,
  deleting,
}: {
  entry: SteuerTerminEntry;
  onDelete: (() => void) | null;
  deleting: boolean;
}) {
  const sev = SEVERITY_BADGE[entry.severity];
  return (
    <tr className={`${sev.rowCls} text-slate-100`}>
      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">
        <div className="font-medium">{formatDate(entry.datum)}</div>
        <div className="text-xs text-slate-400">
          {entry.days_until >= 0 ? `in ${entry.days_until} T` : `${Math.abs(entry.days_until)} T überf.`}
        </div>
      </td>
      <td className="px-4 py-2.5">{entry.firma_display}</td>
      <td className="px-4 py-2.5">
        <div className="font-medium">{entry.art_display}</div>
        {entry.notiz && <div className="text-xs text-slate-400 max-w-xs truncate">{entry.notiz}</div>}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
        {formatEUR(entry.betrag_eur)}
      </td>
      <td className="px-4 py-2.5 text-xs text-slate-400 max-w-[12rem] truncate">{entry.quelle}</td>
      <td className="px-4 py-2.5">
        <span className={`inline-block px-2 py-0.5 rounded-full border text-xs ${sev.cls}`}>
          {sev.label}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        {onDelete && (
          <button
            onClick={() => {
              if (confirm("Eintrag wirklich löschen?")) onDelete();
            }}
            disabled={deleting}
            className="text-rose-300 hover:text-rose-200 text-xs disabled:opacity-40 min-h-11 px-3"
            title="Manuelle Eintraege loeschbar"
          >
            löschen
          </button>
        )}
      </td>
    </tr>
  );
}

function KpiCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "sky" | "violet";
}) {
  const ring =
    accent === "sky"
      ? "border-sky-500/30"
      : accent === "violet"
        ? "border-violet-500/30"
        : "border-slate-800";
  return (
    <div className={`rounded-2xl border ${ring} bg-slate-950 p-4`}>
      <div className="text-[11px] uppercase tracking-widest text-slate-300">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums text-slate-100">{value}</div>
    </div>
  );
}

function ManualForm({
  onCreated,
  onUnauth,
}: {
  onCreated: () => void;
  onUnauth: () => void;
}) {
  const [firma, setFirma] = useState<HoldingFirma>("drainx");
  const [art, setArt] = useState("ust_va_2026_05");
  const [datum, setDatum] = useState("");
  const [betrag, setBetrag] = useState("");
  const [notiz, setNotiz] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    setOk(null);
    try {
      const req: ManualSteuerTerminRequest = {
        firma_id: firma,
        art,
        datum,
        betrag_eur: parseFloat(betrag.replace(",", ".")),
        notiz: notiz.trim() || null,
      };
      const res = await postManualSteuer(req);
      if (res.persisted) {
        setOk(`Eintrag gespeichert: ${art} · ${datum} · ${betrag} €`);
        setBetrag("");
        setNotiz("");
        onCreated();
      } else {
        setErr("Eintrag bereits vorhanden (Firma + Art + Datum identisch).");
      }
    } catch (e2) {
      if (e2 instanceof UnauthorizedError) {
        onUnauth();
        return;
      }
      setErr(e2 instanceof Error ? e2.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  };

  const labelByFirma = useMemo(() => FIRMA_DISPLAY[firma], [firma]);

  return (
    <section className="rounded-2xl border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.04] to-slate-950 p-5">
      <h2 className="text-base font-semibold text-slate-100 mb-1">
        Manueller Steuer-Eintrag — {labelByFirma}
      </h2>
      <p className="text-xs text-slate-400 mb-4">
        Idempotent über (Firma + Art + Datum). Manuelle Einträge sind später löschbar.
      </p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Firma">
          <select
            value={firma}
            onChange={(e) => setFirma(e.target.value as HoldingFirma)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-3 py-2"
          >
            {FIRMA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Art">
          <select
            value={art}
            onChange={(e) => setArt(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-3 py-2"
          >
            {ART_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Datum (Fälligkeit)">
          <input
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-3 py-2"
            required
          />
        </Field>
        <Field label="Betrag (EUR, brutto)">
          <input
            type="text"
            inputMode="decimal"
            value={betrag}
            onChange={(e) => setBetrag(e.target.value)}
            placeholder="9047.00"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-3 py-2"
            required
          />
        </Field>
        <Field label="Notiz (optional)" wide>
          <input
            type="text"
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="z.B. SOKA-Bau Mai 2026"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 px-3 py-2"
          />
        </Field>
        <div className="sm:col-span-2 flex gap-3 items-center pt-1">
          <button
            type="submit"
            disabled={submitting || !betrag || !datum}
            className="rounded-lg bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white font-semibold px-5 min-h-11"
          >
            {submitting ? "Speichere…" : "Speichern"}
          </button>
          {ok && <span className="text-emerald-300 text-sm">{ok}</span>}
          {err && <span className="text-rose-300 text-sm">{err}</span>}
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="block text-xs font-medium text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-300 text-sm">
      {children}
    </div>
  );
}
